import React from 'react';
import { Form } from 'react-bootstrap';
import { ClipboardCheck } from 'lucide-react';
import { LibraryFormData } from '../types/LibraryTemplate';
import { InspectionLibraryFields, LibraryInspectionCategory } from '../types/LibraryInspectionTemplate';
import {
    BuildingCheckTemplate,
    INTERVAL_UNITS,
    NEXT_DUE_DATE_OPTIONS,
    REGULATORY_TYPE_OPTIONS,
    TEMPLATE_CATEGORIES,
    InspectionCategory,
    RegulatoryType,
} from '../Pages/BuildingChecks/types';
import { parseCsvCost, formatCsvCost } from '../Helpers/csvUtils';
import { LibraryConfig } from './LibraryConfig';
import NumberInput from '../Components/NumberInput';
import ComponentTypeSelect from '../Pages/BuildingChecks/components/ComponentTypeSelect';
import { isStandardComponentType, parseComponentTypeCell } from '../Helpers/componentTypeOptions';
import { getAllBuildComponentTypes, resolveBuildComponentTypeTranslation } from '../Helpers/BuildComponentTypesStore';

type IntervalUnit = BuildingCheckTemplate['interval']['unit'];
type NextDue = BuildingCheckTemplate['nextDueDateCalculation'];
type Measurement = NonNullable<InspectionLibraryFields['takesMeasurement']>;

export const LIBRARY_INSPECTION_CATEGORIES: ReadonlyArray<{ value: LibraryInspectionCategory; label: string }> =
    TEMPLATE_CATEGORIES.filter(
        (c): c is (typeof TEMPLATE_CATEGORIES)[number] & { value: LibraryInspectionCategory } =>
            c.value !== InspectionCategory.ConditionAssessment
    );

const isLibraryCategory = (v: unknown): v is LibraryInspectionCategory =>
    LIBRARY_INSPECTION_CATEGORIES.some((c) => c.value === v);
const isRegulatoryType = (v: unknown): v is RegulatoryType =>
    REGULATORY_TYPE_OPTIONS.some((o) => o.value === v);
const isComponentType = (v: unknown): v is string => typeof v === 'string' && isStandardComponentType(v);
const isIntervalUnit = (v: unknown): v is IntervalUnit => INTERVAL_UNITS.some((u) => u.value === v);
const isNextDue = (v: unknown): v is NextDue => NEXT_DUE_DATE_OPTIONS.some((o) => o.value === v);
const isWholeNonNegative = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

const categoryLabel = (c: LibraryInspectionCategory): string =>
    LIBRARY_INSPECTION_CATEGORIES.find((o) => o.value === c)?.label ?? c;
const regulatoryLabel = (r: RegulatoryType): string => REGULATORY_TYPE_OPTIONS.find((o) => o.value === r)?.label ?? r;

// ============================================================
// CSV spellings
// ============================================================

const INTERVAL_UNIT_TO_CSV: Record<IntervalUnit, string> = {
    days: 'dagar',
    weeks: 'veckor',
    months: 'månader',
    years: 'år',
};
const INTERVAL_UNIT_FROM_CSV: ReadonlyMap<string, IntervalUnit> = new Map(
    (Object.entries(INTERVAL_UNIT_TO_CSV) as Array<[IntervalUnit, string]>).map(([unit, word]) => [word, unit])
);
const PICKER_INTERVAL_LABELS: Record<IntervalUnit, string> = { days: 'd', weeks: 'v', months: 'mån', years: 'år' };

const NEXT_DUE_TO_CSV: Record<NextDue, string> = { scheduled: 'planerat', actual: 'utfört' };

/** Match a cell against an enum's value or Swedish label, case-insensitively. Empty → null. */
function matchOption<T extends string>(
    cell: string,
    options: ReadonlyArray<{ value: T; label: string }>
): T | null | undefined {
    if (!cell) return null;
    const lower = cell.toLowerCase();
    return options.find((o) => o.value === lower || o.label.toLowerCase() === lower)?.value;
}

/** Accepts exactly "<n> dagar|veckor|månader|år". */
function parseCsvInterval(cell: string): BuildingCheckTemplate['interval'] | null {
    const match = cell.match(/^\s*(\d+)\s*(dagar|veckor|månader|år)\s*$/i);
    if (!match) return null;
    const unit = INTERVAL_UNIT_FROM_CSV.get(match[2].toLowerCase());
    const value = parseInt(match[1], 10);
    if (!unit || value <= 0) return null;
    return { value, unit };
}

/** Whole number >= 0, e.g. "30". */
function parseCsvWhole(cell: string): number | null {
    return /^\s*\d+\s*$/.test(cell) ? parseInt(cell, 10) : null;
}

/** Decimal with point or comma, e.g. "-2,5". Empty → undefined. */
function parseCsvDecimal(cell: string): number | null | undefined {
    if (!cell) return undefined;
    return /^-?\d+(?:[.,]\d+)?$/.test(cell) ? parseFloat(cell.replace(',', '.')) : null;
}

const formatCsvDecimal = (n: number | undefined): string => (n === undefined ? '' : String(n).replace('.', ','));

function parseCsvBoolean(cell: string): boolean | null {
    const lower = cell.toLowerCase();
    if (lower === '' || lower === 'nej') return false;
    if (lower === 'ja') return true;
    return null;
}

const csvError = (label: string, detail: string) => ({ ok: false as const, label, detail });

// ============================================================
// Editor fields
// ============================================================

type InspectionForm = LibraryFormData<InspectionLibraryFields>;

/** Measurement without undefined keys (Firebase refuses undefined values). */
function buildMeasurement(metric: string, min: number | undefined, max: number | undefined): Measurement {
    const range: { min?: number; max?: number } = {};
    if (min !== undefined) range.min = min;
    if (max !== undefined) range.max = max;
    return Object.keys(range).length > 0 ? { metric, acceptableRange: range } : { metric };
}

const OptionalNumber: React.FC<{
    label: string;
    value: number | undefined;
    onChange: (value: number | undefined) => void;
    disabled: boolean;
}> = ({ label, value, onChange, disabled }) => (
    <Form.Group>
        <Form.Label>{label}</Form.Label>
        <Form.Control
            type="number"
            step="any"
            value={value ?? ''}
            placeholder="Ingen gräns"
            onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
            disabled={disabled}
        />
    </Form.Group>
);

const InspectionFields: React.FC<{
    form: InspectionForm;
    setForm: React.Dispatch<React.SetStateAction<InspectionForm>>;
    disabled: boolean;
}> = ({ form, setForm, disabled }) => {
    const set = <K extends keyof InspectionLibraryFields>(key: K, value: InspectionLibraryFields[K]) =>
        setForm((prev) => ({ ...prev, [key]: value }));
    const measurement = form.takesMeasurement;
    const setMeasurement = (patch: Partial<{ metric: string; min: number | undefined; max: number | undefined }>) => {
        if (!measurement) return;
        const next = {
            metric: measurement.metric,
            min: measurement.acceptableRange?.min,
            max: measurement.acceptableRange?.max,
            ...patch,
        };
        set('takesMeasurement', buildMeasurement(next.metric, next.min, next.max));
    };

    return (
        <>
            <Form.Group className="mb-3">
                <Form.Label>Beskrivning</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    value={form.description}
                    onChange={(e) => set('description', e.target.value)}
                    disabled={disabled}
                />
            </Form.Group>

            <div className="row g-3 mb-3">
                <div className="col-md-4">
                    <Form.Group>
                        <Form.Label>Kategori</Form.Label>
                        <Form.Select
                            value={form.category}
                            onChange={(e) => set('category', e.target.value as LibraryInspectionCategory)}
                            disabled={disabled}
                        >
                            {LIBRARY_INSPECTION_CATEGORIES.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </div>
                <div className="col-md-4">
                    <Form.Group>
                        <Form.Label>Myndighetskrav</Form.Label>
                        <Form.Select
                            value={form.regulatoryType ?? ''}
                            onChange={(e) =>
                                set('regulatoryType', e.target.value ? (e.target.value as RegulatoryType) : null)
                            }
                            disabled={disabled}
                        >
                            <option value="">Inget</option>
                            {REGULATORY_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </div>
                <div className="col-md-4">
                    <Form.Group>
                        <Form.Label htmlFor="inspection-library-component-type">Komponenttyp</Form.Label>
                        <ComponentTypeSelect
                            id="inspection-library-component-type"
                            value={form.componentSpecificType}
                            onChange={(value) => set('componentSpecificType', value)}
                            disabled={disabled}
                            noneLabel="Ingen (hela fastigheten)"
                        />
                    </Form.Group>
                </div>
            </div>

            <div className="row g-3 mb-3">
                <div className="col-md-3">
                    <Form.Group>
                        <Form.Label>Intervall</Form.Label>
                        <NumberInput
                            className="form-control"
                            min="1"
                            step="1"
                            integer
                            emptyValue={1}
                            value={form.interval.value}
                            onChange={(value) => set('interval', { ...form.interval, value })}
                            disabled={disabled}
                        />
                    </Form.Group>
                </div>
                <div className="col-md-3">
                    <Form.Group>
                        <Form.Label>Enhet</Form.Label>
                        <Form.Select
                            value={form.interval.unit}
                            onChange={(e) => set('interval', { ...form.interval, unit: e.target.value as IntervalUnit })}
                            disabled={disabled}
                        >
                            {INTERVAL_UNITS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </div>
                <div className="col-md-6">
                    <Form.Group>
                        <Form.Label>Nästa förfallodatum</Form.Label>
                        <Form.Select
                            value={form.nextDueDateCalculation}
                            onChange={(e) => set('nextDueDateCalculation', e.target.value as NextDue)}
                            disabled={disabled}
                        >
                            {NEXT_DUE_DATE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </div>
            </div>

            <div className="row g-3 mb-3">
                <div className="col-md-4">
                    <Form.Group>
                        <Form.Label>Tidsåtgång (min)</Form.Label>
                        <NumberInput
                            className="form-control"
                            min="0"
                            step="1"
                            integer
                            value={form.estimatedTime}
                            onChange={(value) => set('estimatedTime', value)}
                            disabled={disabled}
                        />
                    </Form.Group>
                </div>
                <div className="col-md-4">
                    <Form.Group>
                        <Form.Label>Kostnad (kr)</Form.Label>
                        <NumberInput
                            className="form-control"
                            min="0"
                            step="1"
                            integer
                            value={form.estimatedCost}
                            onChange={(value) => set('estimatedCost', value)}
                            disabled={disabled}
                        />
                    </Form.Group>
                </div>
                <div className="col-md-4">
                    <Form.Group>
                        <Form.Label>Förvarning (dagar)</Form.Label>
                        <NumberInput
                            className="form-control"
                            min="0"
                            step="1"
                            integer
                            value={form.advanceNotice}
                            onChange={(value) => set('advanceNotice', value)}
                            disabled={disabled}
                        />
                    </Form.Group>
                </div>
            </div>

            <Form.Check
                type="checkbox"
                id="inspection-library-photo-required"
                className="mb-3"
                label="Foto krävs"
                checked={form.photoRequired}
                onChange={(e) => set('photoRequired', e.target.checked)}
                disabled={disabled}
            />

            <Form.Check
                type="checkbox"
                id="inspection-library-measurement"
                className="mb-2"
                label="Mätvärde registreras"
                checked={measurement !== null}
                onChange={(e) => set('takesMeasurement', e.target.checked ? { metric: 'mm' } : null)}
                disabled={disabled}
            />
            {measurement && (
                <div className="row g-3 mb-3">
                    <div className="col-md-4">
                        <Form.Group>
                            <Form.Label>Enhet för mätvärde</Form.Label>
                            <Form.Control
                                type="text"
                                value={measurement.metric}
                                placeholder="T.ex. mm, kg, °C"
                                onChange={(e) => setMeasurement({ metric: e.target.value })}
                                disabled={disabled}
                            />
                        </Form.Group>
                    </div>
                    <div className="col-md-4">
                        <OptionalNumber
                            label="Min"
                            value={measurement.acceptableRange?.min}
                            onChange={(min) => setMeasurement({ min })}
                            disabled={disabled}
                        />
                    </div>
                    <div className="col-md-4">
                        <OptionalNumber
                            label="Max"
                            value={measurement.acceptableRange?.max}
                            onChange={(max) => setMeasurement({ max })}
                            disabled={disabled}
                        />
                    </div>
                </div>
            )}

        </>
    );
};

// ============================================================
// Config
// ============================================================

/**
 * Kontroll library: Kontrollmall defaults that admins copy when creating a
 * Kontrollmall (BuildingCheckTemplateModal, "Kopiera från bibliotek").
 * No base types; grouped by kontroll category.
 */
export const inspectionLibrary: LibraryConfig<InspectionLibraryFields> = {
    path: 'config/inspectionLibraryTemplates',
    usesBaseTypes: false,
    grouping: {
        viewLabel: 'Gruppera efter kategori',
        groups: LIBRARY_INSPECTION_CATEGORIES.map((c) => ({ key: c.value, label: c.label })),
        groupsOf: (t) => [t.category],
    },
    fieldKeys: [
        'description',
        'category',
        'regulatoryType',
        'componentSpecificType',
        'takesMeasurement',
        'interval',
        'nextDueDateCalculation',
        'photoRequired',
        'estimatedTime',
        'estimatedCost',
        'advanceNotice',
    ],
    defaultFields: {
        description: '',
        category: InspectionCategory.RoutineOperations,
        regulatoryType: null,
        componentSpecificType: null,
        takesMeasurement: null,
        interval: { value: 1, unit: 'years' },
        nextDueDateCalculation: 'scheduled',
        photoRequired: false,
        estimatedTime: 0,
        estimatedCost: 0,
        advanceNotice: 30,
    },

    parseFields(d, key) {
        if (!isLibraryCategory(d.category)) {
            console.warn(`Invalid inspection library template ${key}: invalid category`);
            return null;
        }
        const interval = d.interval as Record<string, unknown> | undefined;
        if (!interval || typeof interval.value !== 'number' || !isIntervalUnit(interval.unit)) {
            console.warn(`Invalid inspection library template ${key}: invalid interval`);
            return null;
        }

        let takesMeasurement: Measurement | null = null;
        const m = d.takesMeasurement as Record<string, unknown> | undefined;
        if (m && typeof m.metric === 'string') {
            const range = m.acceptableRange as Record<string, unknown> | undefined;
            takesMeasurement = buildMeasurement(
                m.metric,
                typeof range?.min === 'number' ? range.min : undefined,
                typeof range?.max === 'number' ? range.max : undefined
            );
        }

        return {
            description: typeof d.description === 'string' ? d.description : '',
            category: d.category,
            regulatoryType: isRegulatoryType(d.regulatoryType) ? d.regulatoryType : null,
            componentSpecificType: isComponentType(d.componentSpecificType) ? d.componentSpecificType : null,
            takesMeasurement,
            interval: { value: interval.value, unit: interval.unit },
            nextDueDateCalculation: isNextDue(d.nextDueDateCalculation) ? d.nextDueDateCalculation : 'scheduled',
            photoRequired: d.photoRequired === true,
            estimatedTime: typeof d.estimatedTime === 'number' ? d.estimatedTime : 0,
            estimatedCost: typeof d.estimatedCost === 'number' ? d.estimatedCost : 0,
            advanceNotice: typeof d.advanceNotice === 'number' ? d.advanceNotice : 30,
        };
    },

    validateFields(f, mode) {
        const check = <K extends keyof InspectionLibraryFields>(key: K) => mode === 'full' || f[key] !== undefined;

        if (check('category') && !isLibraryCategory(f.category)) {
            throw new Error('Ogiltig kategori');
        }
        if (check('interval')) {
            if (!f.interval || !Number.isInteger(f.interval.value) || f.interval.value <= 0) {
                throw new Error('Intervallet måste vara ett heltal större än 0');
            }
            if (!isIntervalUnit(f.interval.unit)) {
                throw new Error('Ogiltig intervallenhet');
            }
        }
        if (check('regulatoryType') && f.regulatoryType != null && !isRegulatoryType(f.regulatoryType)) {
            throw new Error('Ogiltigt myndighetskrav');
        }
        if (
            check('componentSpecificType') &&
            f.componentSpecificType != null &&
            !isComponentType(f.componentSpecificType)
        ) {
            throw new Error('Ogiltig komponenttyp (bara standardtyper)');
        }
        if (check('nextDueDateCalculation') && !isNextDue(f.nextDueDateCalculation)) {
            throw new Error('Ogiltigt val för nästa förfallodatum');
        }
        if (check('estimatedTime') && !isWholeNonNegative(f.estimatedTime)) {
            throw new Error('Tidsåtgången måste vara ett heltal, 0 eller större');
        }
        if (check('estimatedCost') && !isWholeNonNegative(f.estimatedCost)) {
            throw new Error('Kostnaden måste vara ett heltal, 0 eller större');
        }
        if (check('advanceNotice') && !isWholeNonNegative(f.advanceNotice)) {
            throw new Error('Förvarningen måste vara ett heltal, 0 eller större');
        }
        if (check('takesMeasurement') && f.takesMeasurement) {
            const { metric, acceptableRange } = f.takesMeasurement;
            if (!metric.trim()) {
                throw new Error('Enhet för mätvärde saknas');
            }
            if (
                acceptableRange?.min !== undefined &&
                acceptableRange.max !== undefined &&
                acceptableRange.min > acceptableRange.max
            ) {
                throw new Error('Min får inte vara större än max');
            }
        }
    },

    labels: {
        title: 'Kontrollbibliotek',
        description: 'Hantera mallar som administratörer kan kopiera när de skapar kontrollmallar',
        icon: ClipboardCheck,
        loadError: 'Kunde inte ladda kontrollbiblioteket',
        pickerTitle: 'Välj kontroll från bibliotek',
        addNamePlaceholder: 'T.ex. Kontroll av brandsläckare',
        deleteNote: 'Redan kopierade kontrollmallar påverkas inte.',
        resetNoun: 'kontrollbiblioteket',
        csvHelp:
            'CSV-filen ska ha kolumnerna: Namn, Beskrivning, Kategori (Avläsningar/Löpande drift/Service), ' +
            'Myndighetskrav (Brand/OVK/Hiss/Dricksvatten/Taksäkerhet/Annat eller tomt), ' +
            'Komponenttyp (typens nyckel eller svenska namn, t.ex. Brandsläckare, eller tomt), Intervall (t.ex. "12 månader"), ' +
            'Förfallodatum från (planerat/utfört), Foto krävs (ja/nej), Tidsåtgång (min), ' +
            'Kostnad (heltal i kr), Förvarning (dagar), ' +
            'Mätvärde (enhet, tomt = inget), Min, Max. Filer med fel i någon rad importeras inte.',
        exportFilePrefix: 'kontrollbibliotek',
    },

    csv: {
        columns: [
            'Beskrivning',
            'Kategori',
            'Myndighetskrav',
            'Komponenttyp',
            'Intervall',
            'Förfallodatum från',
            'Foto krävs',
            'Tidsåtgång (min)',
            'Kostnad',
            'Förvarning (dagar)',
            'Mätvärde',
            'Min',
            'Max',
        ],
        parseColumns([
            description,
            categoryCell,
            regulatoryCell,
            typeCell,
            intervalCell,
            nextDueCell,
            photoCell,
            timeCell,
            costCell,
            noticeCell,
            metric,
            minCell,
            maxCell,
        ]) {
            const category = matchOption(categoryCell, LIBRARY_INSPECTION_CATEGORIES);
            if (!category) {
                return csvError('Ogiltig kategori', `Ogiltig kategori: "${categoryCell}" (Avläsningar, Löpande drift eller Service)`);
            }
            const regulatoryType = matchOption(regulatoryCell, REGULATORY_TYPE_OPTIONS);
            if (regulatoryType === undefined) {
                return csvError('Okänt myndighetskrav', `Okänt myndighetskrav: "${regulatoryCell}"`);
            }
            const componentSpecificType = parseComponentTypeCell(typeCell, getAllBuildComponentTypes());
            if (componentSpecificType === undefined) {
                return csvError('Okänd komponenttyp', `Okänd komponenttyp: "${typeCell}" (bara standardtyper)`);
            }
            const interval = parseCsvInterval(intervalCell);
            if (!interval) {
                return csvError(
                    'Ogiltigt intervall',
                    intervalCell ? `Ogiltigt intervall: "${intervalCell}" (t.ex. "12 månader")` : 'Tomt intervall'
                );
            }
            const nextDue = nextDueCell
                ? (Object.entries(NEXT_DUE_TO_CSV) as Array<[NextDue, string]>).find(
                      ([, word]) => word === nextDueCell.toLowerCase()
                  )?.[0]
                : 'scheduled';
            if (!nextDue) {
                return csvError('Ogiltigt förfallodatum', `Förfallodatum från: "${nextDueCell}" (planerat eller utfört)`);
            }
            const photoRequired = parseCsvBoolean(photoCell);
            if (photoRequired === null) {
                return csvError('Ogiltigt fotokrav', `Foto krävs: "${photoCell}" (ja eller nej)`);
            }
            const estimatedTime = timeCell ? parseCsvWhole(timeCell) : 0;
            if (estimatedTime === null) {
                return csvError('Ogiltig tidsåtgång', `Ogiltig tidsåtgång: "${timeCell}" (heltal i minuter)`);
            }
            const estimatedCost = costCell ? parseCsvCost(costCell) : 0;
            if (estimatedCost === null) {
                return csvError('Ogiltig kostnad', `Ogiltig kostnad: "${costCell}" (endast heltal i kr, t.ex. "2500 kr")`);
            }
            const advanceNotice = noticeCell ? parseCsvWhole(noticeCell) : 30;
            if (advanceNotice === null) {
                return csvError('Ogiltig förvarning', `Ogiltig förvarning: "${noticeCell}" (heltal i dagar)`);
            }
            const min = parseCsvDecimal(minCell);
            const max = parseCsvDecimal(maxCell);
            if (min === null || max === null) {
                return csvError('Ogiltigt min/max', `Ogiltigt min/max: "${minCell}" / "${maxCell}" (t.ex. "2,5")`);
            }
            if (!metric && (min !== undefined || max !== undefined)) {
                return csvError('Mätvärde saknas', 'Min eller max är ifyllt men Mätvärde (enhet) saknas');
            }
            if (min !== undefined && max !== undefined && min > max) {
                return csvError('Ogiltigt min/max', `Min (${minCell}) är större än max (${maxCell})`);
            }

            return {
                ok: true,
                fields: {
                    description,
                    category,
                    regulatoryType,
                    componentSpecificType,
                    takesMeasurement: metric ? buildMeasurement(metric, min, max) : null,
                    interval,
                    nextDueDateCalculation: nextDue,
                    photoRequired,
                    estimatedTime,
                    estimatedCost,
                    advanceNotice,
                },
            };
        },
        formatColumns(t) {
            return [
                t.description,
                categoryLabel(t.category),
                t.regulatoryType ? regulatoryLabel(t.regulatoryType) : '',
                t.componentSpecificType ?? '',
                `${t.interval.value} ${INTERVAL_UNIT_TO_CSV[t.interval.unit]}`,
                NEXT_DUE_TO_CSV[t.nextDueDateCalculation],
                t.photoRequired ? 'ja' : 'nej',
                String(t.estimatedTime),
                formatCsvCost(t.estimatedCost),
                String(t.advanceNotice),
                t.takesMeasurement?.metric ?? '',
                formatCsvDecimal(t.takesMeasurement?.acceptableRange?.min),
                formatCsvDecimal(t.takesMeasurement?.acceptableRange?.max),
            ];
        },
    },

    renderFields(form, setForm, disabled) {
        return <InspectionFields form={form} setForm={setForm} disabled={disabled} />;
    },

    formatSummary(t) {
        const interval = `${t.interval.value} ${PICKER_INTERVAL_LABELS[t.interval.unit]}`;
        const parts = [interval];
        if (t.regulatoryType) parts.push(regulatoryLabel(t.regulatoryType));
        const typeName = resolveBuildComponentTypeTranslation(t.componentSpecificType);
        if (typeName) parts.push(typeName);
        return parts.join(' · ');
    },
};
